import { Node, Viewport, useOnViewportChange } from 'reactflow';
import {
  Portal,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Box,
} from '@chakra-ui/react';
import { Blocks, GroupIcon, ArrowRight } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useAddObjectButton } from './useAddObjectButton';
import { useAddContainerButton } from '../../containers/utils/useAddContainerButton';

export function ContainerizedNodeOutgoingMenu({
  node,
  isHoveredOnNode,
}: {
  node: Node;
  isHoveredOnNode: boolean;
}) {
  const [menu, setMenu] = useState(false);
  const [isHoveredNextToNode, setIsHoveredNextToNode] = useState(false);
  const shouldShowIcons = isHoveredNextToNode || isHoveredOnNode || menu;

  useOnViewportChange({
    onStart: useCallback((_viewport: Viewport) => {
      setMenu(false);
    }, []),
  });

  const nodeId = node.id;

  const useAddEmptyBlock = (shouldRenderOutside: boolean) => {
    return useAddObjectButton({
      id: nodeId,
      sourceHandle: nodeId,
      isContainerizedNode: true,
      shouldRenderOutside: shouldRenderOutside,
    });
  };

  const emptyContainer = useAddContainerButton({
    id: nodeId,
    sourceHandle: nodeId,
  });

  const nodeHeight = node.height ?? 53; // 53 px is the height of empty node
  return (
    <Box
      pos="relative"
      role="group"
      zIndex={999}
      onClick={(ev) => {
        ev.stopPropagation();
      }}
    >
      <Flex
        role="group"
        position="absolute"
        w="13%"
        right={'-40px'}
        top={(nodeHeight / 60) * -1}
        // border={'1px solid red'} // useful for visual debugging
        h={nodeHeight - nodeHeight / 20}
        onMouseEnter={() => setIsHoveredNextToNode(true)}
        onMouseLeave={() => setIsHoveredNextToNode(false)}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Menu
          onOpen={() => setMenu(true)}
          onClose={() => setMenu(false)}
          isOpen={menu}
          closeOnBlur={true}
          closeOnSelect={true}
        >
          <MenuButton
            onClick={(ev) => {
              ev.stopPropagation(); // prevent the click from bubbling up to the parent and focusing the node in the editor. Focusing opens the right sidebar, which we don't want.
              return false;
            }}
            as={IconButton}
            left={'10px'}
            color="fg.default"
            zIndex={999}
            bgColor="white"
            visibility={shouldShowIcons ? 'visible' : 'hidden'}
            _hover={{ bgColor: '#F4F8FA' }}
            isRound={true}
            size="sm"
            variant="outline"
            aria-label="Add object"
            icon={<ArrowRight size="20" />}
          />
          <Portal>
            <MenuList
              onMouseLeave={() => setMenu(false)}
              maxHeight="320px"
              overflowY="auto"
              overflowX="hidden"
            >
              <MenuGroup color="gray.400" title="INSIDE">
                <MenuItem onClick={useAddEmptyBlock(false)} icon={<Blocks size="16px" />}>
                  Empty block
                </MenuItem>
              </MenuGroup>
              <MenuGroup color="gray.400" title="OUTSIDE">
                <MenuItem onClick={useAddEmptyBlock(true)} icon={<Blocks size="16px" />}>
                  Empty block
                </MenuItem>
                <MenuItem icon={<GroupIcon size="16px" />} onClick={emptyContainer}>
                  Container
                </MenuItem>
              </MenuGroup>
            </MenuList>
          </Portal>
        </Menu>
      </Flex>
    </Box>
  );
}
